import React, { useContext, useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react";
import PositionsTable from "../components/PositionsTable";
import StrategyMenu from "../components/StrategyMenu";
import ABWebSocketContext from "../context/ABWebsocketContext";
import AnalyseGraph from "../components/AnalyseGraph";
import { useGraphContext } from "../context/GraphContext";
import StrategyGraph from "../components/StrategyGraph";
import { useTheme } from "../context/ThemeContext";
import WebsocketWrapperContext from "../context/WebsocketWrapperContext";

const AnalyseModal = ({
  isOpen,
  onClose,
  data,
  selectedData,
  selectedSymbol,
  type,
  index,
  allOptionData,
  closeCallBack,
}) => {
  const { theme } = useTheme();
  const { symbolData } = useContext(WebsocketWrapperContext);

  // Memoize expensive calculations
  const memoizedData = useMemo(() => data, [data]);
  const memoizedSelectedData = useMemo(() => selectedData, [selectedData]);
  const memoizedAllOptionData = useMemo(() => allOptionData, [allOptionData]);

  // Memoize LTP calculation
  const ltp = useMemo(() => {
    return symbolData?.[selectedSymbol?.code]?.ltp;
  }, [symbolData, selectedSymbol?.code]);

  // Memoize least expiry calculation
  const leastExpiry = useMemo(() => {
    return selectedData?.[0]?.["expiry_date"];
  }, [selectedData]);

  // Use useCallback for event handlers
  const handleClose = useCallback(() => {
    if (closeCallBack) {
      closeCallBack();
    }
    if (onClose) {
      onClose();
    }
  }, [closeCallBack, onClose]);

  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape' && isOpen) {
      handleClose();
    }
  }, [handleClose, isOpen]);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // Handle escape key press with optimized cleanup
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Use requestAnimationFrame to prevent layout thrashing
      const rafId = requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
      });

      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
        cancelAnimationFrame(rafId);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, handleEscapeKey]);

  // Early return if modal is closed
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}>
      <div className="bg-p400 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] min-h-[80vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 pb-0 ">
          <h1 className="text-[16px] font-semibold text-p300">
            Analyse positions
          </h1>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-3 overflow-y-auto max-h-[calc(90vh-120px)] no-scrollbar">
          <div className="flex flex-col justify-center items-center gap-2">
            <div className="border border-n100 rounded-md shadow-sm w-full">
              <StrategyMenu data={memoizedData} />
            </div>
            <div className="border border-n100 rounded-md shadow-sm w-full max-w-5xl mx-auto">
              {/* Header */}
              <div className="flex justify-between items-center p-4 py-2 border-b border-n100">
                <h2 className="text-[14px] font-medium text-p300">Payoff</h2>
                <div className="flex items-center space-x-6">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <div className="flex flex-row gap-[3px]">
                      <div className="bg-red rounded-l-md w-[7px] h-[3px]"></div>
                      <div className="bg-p150 rounded-r-md w-[7px] h-[3px]"></div>
                    </div>
                    <p className="text-p300 leading-[12px] text-[12px] font-medium">
                      Expiry
                    </p>
                  </div>
                  <div className="flex flex-row items-center justify-center gap-2">
                    <div className="bg-blue rounded-md w-[7px] h-[3px]"></div>
                    <p className="text-p300 leading-[12px] text-[12px] font-medium">
                      Target Date
                    </p>
                  </div>
                </div>
              </div>

              {/* Strategy Graph - Only render when data is available */}
              {memoizedSelectedData && (
                <StrategyGraph
                  data={memoizedData}
                  selectedData={memoizedSelectedData}
                  ltp={ltp}
                  leastExpiry={leastExpiry}
                  allOptionData={memoizedAllOptionData}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AnalyseModal);